import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useAuthCtx } from "../lib/auth-provider";
import { useActiveSubscription } from "../hooks/use-subscription";
import { isManualSub, expirationLabel } from "../lib/manual-sub";
import { updateUserProfile } from "../lib/callables";
import { userLabel } from "../lib/user-label";
import { Spinner } from "../components/spinner";
import { useTranslation } from "react-i18next";
import { APPS } from "@acruxsoft/platform-shared";
import { useMyLicenses } from "../hooks/use-my-licenses";
import { appAccess, licenseEndsAt } from "../lib/app-access";

export function AccountPage() {
  const nav = useNavigate();
  const { user, claims, loading } = useAuthCtx();
  const { sub, loading: subLoading } = useActiveSubscription();
  const licenses = useMyLicenses();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!loading && !user) nav("/login?return=/account", { replace: true });
  }, [user, loading]);

  // Wait until BOTH the auth state is hydrated (claims set or user definitively null)
  // and the subscription listener has produced its first snapshot. Without the
  // !claims guard, a logged-in user briefly sees plan="free" between the moment
  // loading flips to false and the moment claims arrive (the AuthProvider sets
  // user before awaiting getIdTokenResult, so there's a render window with
  // user-set + claims-null + loading-still-true; if anything pushes loading to
  // false before claims, the page renders with "free" defaults for a frame).
  if (loading || subLoading || licenses.loading || (user && !claims)) return <Spinner />;

  const plan = claims?.plan ?? "free";
  const status = claims?.subscriptionStatus ?? (plan === "free" ? "free" : "—");
  const hasPaidSub = !!sub && (sub.status === "active" || sub.status === "past_due");
  // PayPal is dormant, so the plan section only shows for the few subscriptions
  // still inside a paid period — a cancelled one keeps access until it ends.
  const subInPeriod =
    hasPaidSub || (!!sub && sub.status === "cancelled" && sub.currentPeriodEnd > Date.now());
  const manual = isManualSub(sub);
  const dateLabelKey = sub ? expirationLabel(sub) : "renewsOn";
  // "Period started" doubles as "Contract started" on the first cycle and
  // "Last renewal" once PayPal has rolled the period at least once. Heuristic:
  // if currentPeriodStart is within a day of createdAt, treat it as first cycle
  // (covers the lag between sub creation and the activation webhook).
  const isFirstCycle = !!sub && Math.abs(sub.currentPeriodStart - sub.createdAt) < 86400_000;
  const startedLabelKey = isFirstCycle ? "account.contractStarted" : "account.lastRenewal";
  const fmtDate = (ms: number) =>
    new Date(ms).toLocaleDateString(i18n.language, { year: "numeric", month: "long", day: "numeric" });

  const statusLabel = (() => {
    const known = ["active", "past_due", "pending", "cancelled", "free"];
    return known.includes(status) ? t(`account.status.${status}`) : t("account.status.unknown");
  })();

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <div className="acx__card">
        <header className="acx__card-header">
          <span className="acx__card-eyebrow">{t("account.eyebrow")}</span>
          <h1 className="acx__card-title">{user ? userLabel(user) : ""}</h1>
        </header>

        <div className="acx__stack acx__stack--lg">
          {user && <ProfileSection user={user} />}

          <section className="acx__stack">
            <h2 style={{ fontSize: "var(--acx-fs-md)" }}>{t("account.apps.header")}</h2>
            <div className="acx__info-grid">
              {APPS.filter((app) => app.available).map((app) => {
                const now = Date.now();
                const grant = licenses.grantFor(app.appId);
                const access = appAccess({
                  app,
                  emailVerified: user?.emailVerified ?? false,
                  entitlements: claims?.entitlements ?? [],
                  grant,
                  request: licenses.requestFor(app.appId),
                  now
                });
                const ends = licenseEndsAt(grant, now);
                const value =
                  access === "unverified"
                    ? t("account.apps.unverified")
                    : app.access === "free"
                      ? t("account.apps.free")
                      : access === "open"
                        ? ends ? t("account.apps.until", { date: fmtDate(ends) }) : t("account.apps.licensed")
                        : access === "requested"
                          ? t("account.apps.requested")
                          : access === "expired"
                            ? t("account.apps.expired", { date: fmtDate(grant!.expiresAt) })
                            : t("account.apps.none");
                return (
                  <div className="acx__info-item" key={app.appId}>
                    <span className="acx__info-label">{t(`home.apps.${app.appId}.name`)}</span>
                    <span className="acx__info-value">{value}</span>
                  </div>
                );
              })}
            </div>
            <div className="acx__row">
              <Link to="/home" className="acx__btn acx__btn--ghost">{t("account.apps.manage")}</Link>
            </div>
          </section>

          {subInPeriod && (
          <section className="acx__stack">
            <div className="acx__row" style={{ justifyContent: "space-between" }}>
              <h2 style={{ fontSize: "var(--acx-fs-md)" }}>{t("account.planHeader")}</h2>
              <span className={`acx__pill acx__pill--${pillVariant(status)}`}>{statusLabel}</span>
            </div>
            <div className="acx__info-grid">
              {/* Top row: plan + billing interval. */}
              <div className="acx__info-item">
                <span className="acx__info-label">{t("account.planLabel")}</span>
                <span className="acx__info-value" style={{ letterSpacing: "var(--acx-tracking-wide)" }}>{t(`account.planName.${plan}`, { defaultValue: plan.toUpperCase() })}</span>
              </div>
              <div className="acx__info-item">
                <span className="acx__info-label">{t("account.billingInterval")}</span>
                <span className="acx__info-value">{
                  hasPaidSub && sub
                    ? (sub.interval === "monthly" ? t("account.interval.monthly")
                      : sub.interval === "yearly" ? t("account.interval.yearly")
                      : sub.interval)
                    : "—"
                }</span>
              </div>
              {/* Bottom row: period started + renewal/expiry. */}
              {hasPaidSub && sub && sub.currentPeriodStart > 0 && (
                <div className="acx__info-item">
                  <span className="acx__info-label">{t(startedLabelKey)}</span>
                  <span className="acx__info-value">{fmtDate(sub.currentPeriodStart)}</span>
                </div>
              )}
              {subInPeriod && sub && sub.currentPeriodEnd > 0 && (
                <div className="acx__info-item">
                  <span className="acx__info-label">{t(`account.${hasPaidSub ? dateLabelKey : "endsOn"}`)}</span>
                  <span className="acx__info-value">{fmtDate(sub.currentPeriodEnd)}</span>
                </div>
              )}
            </div>
          </section>
          )}

          {hasPaidSub && sub!.status === "active" && !manual && (
            <div className="acx__row">
              <Link to="/account/cancel" className="acx__btn acx__btn--danger">{t("account.cancelSubscription")}</Link>
            </div>
          )}

          <div style={{ borderTop: "1px solid var(--acx-border)", paddingTop: "var(--acx-s-5)" }} className="acx__row">
            <button
              className="acx__btn acx__btn--ghost"
              onClick={() => signOut(auth).then(() => nav("/login"))}
            >
              {t("account.signOut")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileSection({ user }: { user: { displayName: string | null; reload: () => Promise<void> } }) {
  const { t } = useTranslation();
  const [name, setName] = useState(user.displayName ?? "");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset local state if the auth-side displayName changes underneath us
  // (e.g. another tab updated it, or a page revisit re-hydrates Auth).
  useEffect(() => { setName(user.displayName ?? ""); }, [user.displayName]);

  const trimmed = name.trim();
  const initial = (user.displayName ?? "").trim();
  const dirty = trimmed !== initial;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await updateUserProfile({ displayName: trimmed || null });
      // Refresh the Firebase Auth user so user.displayName reflects the change
      // immediately and other parts of the app see the new value.
      await user.reload();
      setSavedAt(Date.now());
    } catch (e) {
      console.error("[account] updateUserProfile failed", e);
      setError(t("account.profile.saveError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="acx__stack">
      <h2 style={{ fontSize: "var(--acx-fs-md)" }}>{t("account.profile.header")}</h2>
      <div className="acx__field">
        <label htmlFor="acx-account-name" className="acx__label">{t("account.profile.nameLabel")}</label>
        <input
          id="acx-account-name"
          className="acx__input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("account.profile.namePlaceholder")}
          maxLength={80}
          autoComplete="name"
          disabled={busy}
        />
      </div>
      <div className="acx__row" style={{ alignItems: "center", gap: "var(--acx-s-3)" }}>
        <button type="button" className="acx__btn acx__btn--primary" onClick={save} disabled={busy || !dirty}>
          {busy ? t("account.profile.saving") : t("account.profile.save")}
        </button>
        {savedAt && !dirty && !busy && (
          <span style={{ color: "var(--acx-success)", fontSize: "var(--acx-fs-sm)" }}>
            {t("account.profile.saved")}
          </span>
        )}
        {error && (
          <span className="acx__alert acx__alert--danger" role="alert">{error}</span>
        )}
      </div>
    </section>
  );
}

function pillVariant(status: string): "active" | "past_due" | "pending" | "cancelled" | "free" | "neutral" {
  if (status === "active") return "active";
  if (status === "past_due") return "past_due";
  if (status === "pending") return "pending";
  if (status === "cancelled") return "cancelled";
  if (status === "free") return "free";
  return "neutral";
}
